import React from "react";

/*
 * The Suggested Orders list of the order zone when BlinkScribe sent an order-suggestions-v2 payload for this
 * visit (N3/orderV2.js; enabled providers only). Replaces the LiveScribe names list in the Suggested card:
 *   each suggestion: a checkbox (ticked when BlinkScribe pre-checked it: flu vaccine after the final pass),
 *     the order name (a variant picker when the catalogue has several), its evidence chip and the reason,
 *     and the card's quiet ⊕ add; "✓ Added" once it is on the visit
 *   "Add checked (n)": adds every ticked row through the same add as ⊕ (the provider's action; nothing is
 *     ordered by itself)
 *   Discussed: deferred / declined / past-result items, greyed, never addable from here
 *   Already placed: orders already on today's visit (before or during the recording), never suggested again
 *   More from LiveScribe: its suggestions outside the study (referrals, outside imaging, prescriptions), as
 *     before: the plain ⊕ add
 *   "Review orders" (onReview; providers with the end-of-visit review screen, after the stop): reopens
 *     components/OrderReview (ONE screen, Confirm places the ticked orders)
 */

const SparklesIcon = ({ size = 15 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="m12 3-1.912 5.813a2 2 0 0 1-1.275 1.275L3 12l5.813 1.912a2 2 0 0 1 1.275 1.275L12 21l1.912-5.813a2 2 0 0 1 1.275-1.275L21 12l-5.813-1.912a2 2 0 0 1-1.275-1.275L12 3Z" />
  </svg>
);
const PlusRing = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <circle className="n3-oc-ring" cx="12" cy="12" r="9.6" />
    <path className="n3-oc-pl" d="M12 7.6v8.8M7.6 12h8.8" />
  </svg>
);

const Chip = ({ tone, children, title }) => (
  <span className={`n3-v2chip ${tone || "chart"}`} title={title}>{children}</span>
);

export default function SuggestedV2({ rows, discussed, placed, other = [], final, onToggle, onPick, onAdd, onAddOther, onAddChecked, onDetail, onReview = null }) {
  const checkedOpen = rows.filter((r) => r.checked && !r.added && !r.busy);
  const busy = rows.some((r) => r.busy);
  return (
    <div className="n3-v2" data-testid="n3-v2">
      <div className="n3-ohead sug">
        <span className="n3-ohead-t"><span className="ic-sug"><SparklesIcon /></span>SUGGESTED ORDERS</span>
        <span className="n3-cnt sug">{rows.length}</span>
        {onReview ? (
          <button
            type="button"
            className="n3-v2-addall no-hover-tint"
            onClick={onReview}
            title="Opens the end-of-visit order review: untick what you do not want, then Confirm"
          >
            Review orders
          </button>
        ) : null}
        {checkedOpen.length > 0 ? (
          <button
            type="button"
            className="n3-v2-addall no-hover-tint"
            disabled={busy}
            onClick={() => onAddChecked(checkedOpen)}
            title="Adds every checked suggestion to this visit’s orders"
          >
            Add checked ({checkedOpen.length})
          </button>
        ) : null}
      </div>
      {!final && rows.length > 0 ? <div className="n3-v2-note">Updating as the visit goes on · final check when you stop</div> : null}
      <div className="n3-olist">
        {rows.length === 0 ? <div className="n3-olist-empty">No suggested orders yet</div> : null}
        {rows.map((o) => (
          <div key={o.key} className={`n3-srow n3-v2row${o.added ? " added" : ""}${o.checked ? " checked" : ""}`} data-sug={o.key} data-concept={o.concept}>
            {o.added ? (
              <span className="n3-v2-box done" aria-hidden="true">✓</span>
            ) : (
              <input
                type="checkbox"
                className="n3-v2-box"
                checked={o.checked}
                disabled={o.busy}
                aria-label={`Select ${o.name}`}
                onChange={(e) => onToggle(o, e.target.checked)}
              />
            )}
            <div className="n3-v2-body">
              <div className="n3-v2-line">
                {o.variants.length > 1 && !o.added ? (
                  <select
                    className="n3-v2-variant"
                    aria-label={`Which ${o.name}`}
                    value={o.name}
                    disabled={o.busy}
                    onChange={(e) => onPick(o, e.target.value)}
                  >
                    {o.variants.map((v) => <option key={v} value={v}>{v}</option>)}
                  </select>
                ) : (
                  <button type="button" className="n3-srow-t no-hover-tint" onClick={() => onDetail(o)}>{o.name}</button>
                )}
                <Chip tone={o.tone} title={o.confidence != null ? `Model confidence ${Math.round(o.confidence * 100)}%` : undefined}>
                  {o.evidenceLabel || o.evidence}
                </Chip>
                {o.prechecked && !o.added ? <span className="n3-v2-pre" title="Pre-checked: this order type is ordered almost every time it is agreed in the visit">pre-checked</span> : null}
              </div>
              {o.reason ? <div className="n3-v2-why">{o.reason}{o.chartReason ? ` · ${o.chartReason}` : ""}</div> : null}
            </div>
            {o.added ? (
              <span className="n3-sug-added" title={o.pre ? "Already in Current Orders: added before it was suggested" : undefined}>✓ Added</span>
            ) : (
              <button
                type="button"
                className="n3-sug-add no-hover-tint"
                disabled={o.busy}
                aria-busy={o.busy || undefined}
                aria-label={`Add ${o.name} to this visit’s orders`}
                title={`Adds ${o.name} to this visit’s orders`}
                onClick={() => onAdd(o)}
              >
                <PlusRing />
              </button>
            )}
          </div>
        ))}
      </div>
      {other.length > 0 ? (
        <div className="n3-v2-group" data-testid="n3-v2-other">
          <div className="n3-v2-gh">More from LiveScribe</div>
          {other.map((o) => (
            <div key={o.key} className={`n3-srow${o.added ? " added" : ""}`} data-sug={o.key}>
              <button type="button" className="n3-srow-t no-hover-tint" onClick={() => onDetail(o)}>{o.name}</button>
              {o.added ? (
                <span className="n3-sug-added" title={o.pre ? "Already in Current Orders: added before it was suggested" : undefined}>✓ Added</span>
              ) : (
                <button
                  type="button"
                  className="n3-sug-add no-hover-tint"
                  disabled={o.busy}
                  aria-busy={o.busy || undefined}
                  aria-label={`Add ${o.name} to this visit’s orders`}
                  title={`Adds ${o.name} to this visit’s orders`}
                  onClick={() => onAddOther(o)}
                >
                  <PlusRing />
                </button>
              )}
            </div>
          ))}
        </div>
      ) : null}
      {discussed.length > 0 ? (
        <div className="n3-v2-group" data-testid="n3-v2-discussed">
          <div className="n3-v2-gh">Discussed · not for today</div>
          {discussed.map((d) => (
            <div key={d.concept} className="n3-v2-grow muted" data-concept={d.concept}>
              <span className="n3-v2-gname">{d.order_name}</span>
              <Chip tone="discussed">{d.evidence_label || d.evidence}</Chip>
            </div>
          ))}
        </div>
      ) : null}
      {placed.length > 0 ? (
        <div className="n3-v2-group" data-testid="n3-v2-placed">
          <div className="n3-v2-gh">Already placed today</div>
          {placed.map((d) => (
            <div key={d.concept} className="n3-v2-grow muted" data-concept={d.concept}>
              <span className="n3-v2-gname">{d.order_name}</span>
              <span className="n3-sug-added">✓ Placed</span>
            </div>
          ))}
        </div>
      ) : null}
    </div>
  );
}
